/* eslint-disable max-lines */
import {
    API_ENDPOINTS,
    CHECKOUT_WEB_APP_URL,
    DINEOUT_CART_API_HOST,
    LOG_TABLES,
    SUCCESS_STATUS_CODE,
} from "constants/api";
import { DEEPLINK_FORMAT } from "constants/bill";
import { useCallback, useContext, useRef, useState } from "react";
import { format, isEmpty } from "@swiggy-private/js-utils";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import type { RawOrderData } from "@swiggy-private/native-bridge";
import {
    QrCodeActions,
    ResponseContext,
    SwiggyContextHeader,
    TableContext,
    useApiContextFetch,
} from "@swiggy-private/portal-dineout-context";
import type { IPaymentCard } from "@swiggy-private/portal-dineout-ui";
import { getDefaultApiHeaders } from "utils/api";
import { getBridgeSdk } from "utils/bridgeSdk";
import { isApp } from "utils/browser";
import { orderFailedEvent, storeOrderInfoEvent } from "utils/helper";
import { sendCustomNREvViaBlaze } from "utils/newrelic";
import {
    clearPaymentPendingParams,
    getConfirmOrderParams,
    getPaymentPendingParamsFromLocal,
    getSuccessScreenHttpHeaders,
    setPaymentPendingParams,
} from "utils/order";
import { IOrderResponse, IFailureResponseCard, RazorpayOptions, OrderFailInstance } from "utils/paymentTypes";

/**
 * Hook to orchestrate the dineout external order flow.
 * Covers placing an order, confirming payment (including app relaunch recovery),
 * emitting analytics/bridge events, toggling UI loaders, and handling failure UI.
 */
export const useExternalOrder = () => {
    const makeApiCall = useApiContextFetch();
    const { tableId, restaurantId, parentOrderId } = useContext(TableContext);
    const { dispatch } = useContext(ResponseContext);
    const { swiggyHeaders } = useContext(SwiggyContextHeader);

    const [isPlacingOrderLoading, setIsPlacingOrderLoading] = useState(false);
    const [isConfirmingOrderLoading, setIsConfirmingOrderLoading] = useState(false);
    const [showHoldOnOrderLoader, setshowHoldOnOrderLoader] = useState(false);

    const onLaunchApiCallRef = useRef<boolean>(false);
    const apiCallRef = useRef<boolean>(false); // gate to prevent concurrent API calls

    const orderFailed = useCallback(
        async (
            failInstance: OrderFailInstance,
            finalAmount?: string | number,
            options?: {
                orderId?: string;
                error?: { statusCode?: number; statusMessage?: string } | Error;
            },
        ) => {
            await clearPaymentPendingParams();
            orderFailedEvent({
                orderId: options?.orderId,
                totalBill: finalAmount ? String(finalAmount) : "",
                failInstance,
                error: options?.error,
            });
        },
        [],
    );

    const orderSuccess = useCallback(
        async (response: RawOrderData) => {
            await clearPaymentPendingParams();

            if (isApp()) {
                getBridgeSdk()
                    .orderConfirmedEvent((response as RawOrderData) || {})
                    .catch(() => void 0);
            }

            storeOrderInfoEvent(response, swiggyHeaders?.["version-code"] || "");

            // move to success page
            // @ts-expect-error: order_id will exist on the order object returned from backend
            const orderId = response?.orders?.[0]?.order_id;

            if (!isApp()) {
                window.location.href = `${window.location.origin}/dineout-cart/?state=DINEOUT&orderId=${orderId}&flow=dineoutcart&darkTheme=false`;
            } else {
                //Navigate to success page using deeplink
                getBridgeSdk()
                    ?.navigateToScreen({
                        type: NavigationTypes.DEEPLINK,
                        link: format(
                            DEEPLINK_FORMAT,
                            "false",
                            true,
                            encodeURIComponent(
                                `${CHECKOUT_WEB_APP_URL}/?state=DINEOUT&orderId=${orderId}&flow=dineoutcart&darkTheme=false`,
                            ),
                        ),
                        httpHeaders: getSuccessScreenHttpHeaders(swiggyHeaders as Record<string, string>),
                    })
                    .catch(() => {
                        // no-op
                    });

                /** Commenting now as this will only be used when confirm order is launched for dineout in app */
                // Calling post order event to clear pending params
                // if (isApp()) {
                //     getBridgeSdk()
                //         .postOrderEvent(response)
                //         .catch(() => void 0);
                // }
            }
        },
        [swiggyHeaders],
    );

    /**
     * Initiates order placement with the selected payment method and
     * stores pending params so the confirm step can be retried on relaunch.
     */
    const placeOrder = useCallback(
        async (paymentCard: IPaymentCard) => {
            try {
                if (apiCallRef.current) {
                    return;
                }

                apiCallRef.current = true;
                setIsPlacingOrderLoading(true);

                const response = await makeApiCall?.<IOrderResponse>(
                    {
                        apiHost: DINEOUT_CART_API_HOST,
                        endpoint: API_ENDPOINTS.EXTERNAL_PLACE_ORDER,
                        bodyParams: { tableId, restaurantId, parentOrderId, paymentGateway: paymentCard.type },
                        options: {
                            method: "POST",
                            headers: {
                                ...swiggyHeaders,
                                ...getDefaultApiHeaders(swiggyHeaders),
                            } as HeadersInit,
                        },
                    },
                    { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders) },
                    sendCustomNREvViaBlaze,
                    LOG_TABLES,
                );

                if (isEmpty(response) || response?.statusCode !== SUCCESS_STATUS_CODE || isEmpty(response.data)) {
                    await orderFailed(OrderFailInstance.PLACE, paymentCard.finalAmount, {
                        error: {
                            statusCode: response?.statusCode,
                            statusMessage: response?.statusMessage,
                        },
                    });

                    const { paymentErrorCard } = (response?.data as { paymentErrorCard?: IFailureResponseCard }) || {};

                    dispatch({
                        type: QrCodeActions.SHOW_PAYMENT_FAILURE,
                        payload: {
                            amount: paymentCard.finalAmount,
                            paymentFailureCardData: {
                                title: paymentErrorCard?.title,
                                description: paymentErrorCard?.message,
                                buttonText: paymentErrorCard?.cta?.text,
                                paymentIcon: paymentErrorCard?.icon,
                            },
                        },
                    });

                    return response;
                }

                await setPaymentPendingParams({
                    pendingPaymentParams: getConfirmOrderParams({
                        orderId: (response.data as RazorpayOptions)?.swiggyOrderId,
                        paymentTransactionId: (response.data as RazorpayOptions)?.transactionId,
                        headers: {
                            ...swiggyHeaders,
                            ...getDefaultApiHeaders(swiggyHeaders),
                        } as Record<string, string>,
                    }),
                    paymentMethodName: paymentCard.type,
                    marketplaceBusinessLine: swiggyHeaders?.marketplaceBusinessLine || "",
                    marketplaceCategory: swiggyHeaders?.marketplaceCategory || "",
                });

                return response;
            } catch (error) {
                await orderFailed(OrderFailInstance.PLACE, paymentCard.finalAmount, {
                    error: error as Error,
                });
            } finally {
                setIsPlacingOrderLoading(false);
                apiCallRef.current = false;
            }
        },
        [dispatch, makeApiCall, orderFailed, parentOrderId, restaurantId, swiggyHeaders, tableId],
    );

    /**
     * Confirms payment for a placed order and navigates to success on approval.
     * Shows payment failure card and logs fail analytics when confirmation fails.
     */
    const confirmOrder = useCallback(
        async (placeOrderData: RazorpayOptions, extraParams?: Record<string, string | undefined>) => {
            try {
                if (apiCallRef.current) {
                    return;
                }

                apiCallRef.current = true;
                setIsConfirmingOrderLoading(true);

                setshowHoldOnOrderLoader(true);

                const confirmOrderParams = getConfirmOrderParams({
                    orderId: placeOrderData.swiggyOrderId,
                    paymentTransactionId: placeOrderData.transactionId,
                    paymentId: extraParams?.payment_id,
                    headers: {
                        ...swiggyHeaders,
                        ...getDefaultApiHeaders(swiggyHeaders),
                    } as Record<string, string>,
                });

                const response = await makeApiCall?.<{ data: RawOrderData }>(
                    {
                        apiHost: confirmOrderParams.host,
                        endpoint: confirmOrderParams.endpoint,
                        bodyParams: confirmOrderParams.body,
                        options: {
                            method: confirmOrderParams.method,
                            headers: confirmOrderParams.headers,
                        },
                    },
                    { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders) },
                    sendCustomNREvViaBlaze,
                    LOG_TABLES,
                );

                if (isEmpty(response) || response?.statusCode !== SUCCESS_STATUS_CODE) {
                    dispatch({
                        type: QrCodeActions.SHOW_PAYMENT_FAILURE,
                        payload: {
                            amount: placeOrderData.amount,
                            paymentFailureCardData: {
                                title: (response?.data as IFailureResponseCard)?.title,
                                description: (response?.data as IFailureResponseCard)?.message,
                                buttonText: (response?.data as IFailureResponseCard)?.cta?.text,
                                paymentIcon: (response?.data as IFailureResponseCard)?.icon,
                            },
                        },
                    });

                    await orderFailed(OrderFailInstance.CONFIRM, placeOrderData.amount, {
                        error: {
                            statusCode: response?.statusCode,
                            statusMessage: response?.statusMessage,
                        },
                    });
                    return response;
                }

                await orderSuccess(response?.data as unknown as RawOrderData);
                return response;
            } catch (error) {
                await orderFailed(OrderFailInstance.CONFIRM, placeOrderData.amount, {
                    error: error as Error,
                });
                return null;
            } finally {
                setIsConfirmingOrderLoading(false);
                apiCallRef.current = false;
                setshowHoldOnOrderLoader(false);
            }
        },
        [dispatch, makeApiCall, orderFailed, orderSuccess, swiggyHeaders],
    );

    /**
     * Confirm order on launch, this is used to confirm the order on launch of the app
     * it is fire and forget api call, so no need to return the response
     */
    const confirmOrderOnLaunch = useCallback(async () => {
        if (onLaunchApiCallRef.current || !makeApiCall) {
            return;
        }

        onLaunchApiCallRef.current = true;
        try {
            const params = await getPaymentPendingParamsFromLocal();
            const confirmOrderParams = params?.requestObj;

            if (isEmpty(confirmOrderParams) || !confirmOrderParams) {
                return;
            }

            await clearPaymentPendingParams();

            await makeApiCall?.<IOrderResponse>(
                {
                    apiHost: confirmOrderParams.host,
                    endpoint: confirmOrderParams.endpoint,
                    bodyParams: { ...confirmOrderParams.body, is_repeat_request: true },
                    options: {
                        method: confirmOrderParams.method,
                        headers: confirmOrderParams.headers,
                    },
                },
                { ...swiggyHeaders, ...getDefaultApiHeaders(swiggyHeaders), ...(confirmOrderParams.headers || {}) },
                sendCustomNREvViaBlaze,
                LOG_TABLES,
            );
        } catch (_) {
            //no-op
        }
    }, [makeApiCall, swiggyHeaders]);

    return {
        placeOrder,
        isPlacingOrderLoading,
        confirmOrder,
        isConfirmingOrderLoading,
        onOrderFailed: orderFailed,
        showHoldOnOrderLoader,
        confirmOrderOnLaunch,
        setshowHoldOnOrderLoader,
    };
};
